import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import { CompanyModal } from '../components/CompanyModal';
import { apiService } from '../services/api';
import Swal from 'sweetalert2';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  MoreHorizontal,
  RefreshCw
} from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

const SearchAndFilters = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'success':
        return `
          background: #28a745;
          color: white;
          &:hover { background: #218838; }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      case 'outline':
        return `
          background: transparent;
          color: #00d4aa;
          border: 2px solid #00d4aa;
          &:hover {
            background: #00d4aa;
            color: white;
          }
        `;
      default:
        return `
          background: white;
          color: #333;
          border: 1px solid #e9ecef;
          &:hover { background: #f8f9fa; }
        `;
    }
  }}

  @media (max-width: 768px) {
    flex: 1 1 160px;
    justify-content: center;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.875rem;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const TableWrapper = styled.div`
  max-height: 600px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const TableHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: between;
  align-items: center;
`;

const TableTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    min-width: 900px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #f8f9fa;
  background: #fafafa;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Td = styled.td`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f8f9fa;
  color: #666;
  vertical-align: middle;
`;

const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CompanyAvatar = styled.div<{ $hasLogo?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$hasLogo ? '#fff' : 'linear-gradient(135deg, #00d4aa 0%, #00b894 100%)'};
  color: ${props => props.$hasLogo ? '#333' : 'white'};
  border: ${props => props.$hasLogo ? '1px solid #e9ecef' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.125rem;
`;

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const CompanyDetails = styled.div``;

const CompanyName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
`;

const CompanyOwner = styled.div`
  color: #666;
  font-size: 0.875rem;
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'danger' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `background: #d4edda; color: #155724;`;
      case 'warning':
        return `background: #fff3cd; color: #856404;`;
      case 'danger':
        return `background: #f8d7da; color: #721c24;`;
      case 'info':
        return `background: #d1ecf1; color: #0c5460;`;
      default:
        return `background: #e9ecef; color: #495057;`;
    }
  }}
`;

const ActionMenu = styled.div`
  position: relative;
  display: inline-block;
`;

const ViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.65rem;
  border-radius: 6px;
  color: #00d4aa;
  border: 1px solid #00d4aa;
  background: transparent;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #00d4aa;
    color: white;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: 6px;
  color: #666;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #333;
  }
`;

const DropdownMenu = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'block' : 'none'};
  position: absolute;
  right: 0;
  bottom: 100%;
  margin-bottom: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  min-width: 180px;
  z-index: 1000;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  color: #333;
  font-size: 0.875rem;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

interface Company {
  id: number;
  name: string;
  owner_name: string;
  email: string;
  phone: string;
  logo_url?: string;
  plan?: { id: number; name: string; price: number };
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  created_at: string;
  stores_count: number;
  employees_count: number;
  customers_count: number;
  monthly_revenue: number;
}

export const AdminCompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>(undefined);
  const [plans, setPlans] = useState<any[]>([]);
  const searchTermRef = useRef(searchTerm);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const fetchCompanies = useCallback(async (search?: string) => {
    try {
      setIsLoading(true);
      const response = await apiService.getCompanies({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        plan_id: planFilter !== 'all' ? planFilter : undefined,
        search: search ?? searchTermRef.current ?? undefined
      });

      if (response.success && response.data) {
        // Handle both paginated and non-paginated responses
        const companiesData = response.data.data || response.data;
        setCompanies(Array.isArray(companiesData) ? companiesData : [companiesData]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao carregar empresas',
        confirmButtonColor: '#00d4aa'
      });
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, planFilter]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await apiService.getPlans();
      if (response.success && response.data) {
        setPlans(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchPlans();
  }, [fetchCompanies, fetchPlans]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchCompanies(searchTerm);
      }
    }, 500);
    return () => clearTimeout(debounce);
  }, [searchTerm, fetchCompanies]);

  const filteredCompanies = companies.filter(company => {
    const matchesSearch =
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    const matchesPlan = planFilter === 'all' || company.plan?.name === planFilter;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const stats = {
    total: companies.length,
    active: companies.filter(c => c.status === 'active').length
  };

  const getStatusBadge = (status: string) => {
    const config = {
      active: { variant: 'success' as const, icon: <CheckCircle size={12} />, label: 'Ativo' },
      inactive: { variant: 'info' as const, icon: <XCircle size={12} />, label: 'Inativo' },
      pending: { variant: 'warning' as const, icon: <AlertCircle size={12} />, label: 'Pendente' },
      suspended: { variant: 'danger' as const, icon: <XCircle size={12} />, label: 'Suspenso' }
    };

    const { variant, icon, label } = config[status as keyof typeof config] || config.inactive;

    return (
      <Badge $variant={variant}>
        {icon}
        {label}
      </Badge>
    );
  };

  const handleActionClick = (companyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === companyId ? null : companyId);
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setModalMode('edit');
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleViewDetails = (company: Company) => {
    setSelectedCompany(company);
    setModalMode('view');
    setShowModal(true);
    setActiveDropdown(null);
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      active: 'Ativo',
      inactive: 'Inativo',
      pending: 'Pendente',
      suspended: 'Suspenso'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const handleExport = () => {
    if (filteredCompanies.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nenhum dado',
        text: 'Não há empresas para exportar com os filtros aplicados',
        confirmButtonColor: '#00d4aa'
      });
      return;
    }

    // Criar CSV
    const headers = ['ID', 'Empresa', 'Proprietário', 'Email', 'Telefone', 'Plano', 'Status', 'Lojas', 'Funcionários', 'Clientes'];
    const rows = filteredCompanies.map(company => [
      company.id,
      company.name,
      company.owner_name,
      company.email,
      company.phone,
      company.plan?.name || 'Sem plano',
      getStatusLabel(company.status),
      company.stores_count,
      company.employees_count,
      company.customers_count
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `empresas_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      icon: 'success',
      title: 'Exportado!',
      text: `${filteredCompanies.length} empresas exportadas com sucesso`,
      showConfirmButton: false,
      timer: 2000
    });
  };

  const handleNewCompany = () => {
    setSelectedCompany(undefined);
    setModalMode('create');
    setShowModal(true);
  };

  const handleSuspendCompany = async (company: Company) => {
    const action = company.status === 'suspended' ? 'reativar' : 'suspender';

    let reason = '';
    if (action === 'suspender') {
      const result = await Swal.fire({
        title: '<strong>Suspender Empresa</strong>',
        html: `
          <p>Tem certeza que deseja suspender a empresa <b>${company.name}</b>?</p>
          <textarea id="suspension-reason" class="swal2-textarea" placeholder="Motivo da suspensão (obrigatório)" style="width: 100%; min-height: 100px;"></textarea>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ffc107',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sim, suspender',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
        preConfirm: () => {
          const textarea = document.getElementById('suspension-reason') as HTMLTextAreaElement;
          const value = textarea?.value?.trim();
          if (!value) {
            Swal.showValidationMessage('O motivo da suspensão é obrigatório');
            return false;
          }
          return value;
        }
      });

      if (!result.isConfirmed || !result.value) {
        setActiveDropdown(null);
        return;
      }
      reason = result.value;
    } else {
      const result = await Swal.fire({
        title: '<strong>Reativar Empresa</strong>',
        html: `Tem certeza que deseja reativar a empresa <b>${company.name}</b>?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sim, reativar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      });

      if (!result.isConfirmed) {
        setActiveDropdown(null);
        return;
      }
    }

    try {
      if (action === 'suspender') {
        await apiService.suspendCompany(company.id, reason);
      } else {
        await apiService.activateCompany(company.id);
      }

      await fetchCompanies();

      Swal.fire({
        icon: 'success',
        title: `Empresa ${action === 'reativar' ? 'reativada' : 'suspensa'}!`,
        text: `A empresa foi ${action === 'reativar' ? 'reativada' : 'suspensa'} com sucesso`,
        showConfirmButton: false,
        timer: 2000
      });
    } catch (error) {
      console.error('Error updating company status:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: `Erro ao ${action} empresa`,
        confirmButtonColor: '#00d4aa'
      });
    }
    setActiveDropdown(null);
  };

  const handleDeleteCompany = async (company: Company) => {
    const result = await Swal.fire({
      title: '<strong>Confirmar Exclusão</strong>',
      html: `Tem certeza que deseja excluir a empresa <b>${company.name}</b>?<br/><small style="color: #dc3545;">Esta ação não pode ser desfeita e todos os dados serão perdidos.</small>`,
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir permanentemente',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      width: '500px'
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteCompany(company.id);
        await fetchCompanies();

        Swal.fire({
          icon: 'success',
          title: 'Excluída!',
          text: 'Empresa excluída com sucesso',
          showConfirmButton: false,
          timer: 2000
        });
      } catch (error) {
        console.error('Error deleting company:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao excluir empresa',
          confirmButtonColor: '#00d4aa'
        });
      }
    }
    setActiveDropdown(null);
  };

  const handleModalSubmit = async (formData: any) => {
    try {
      if (modalMode === 'create') {
        await apiService.createCompany(formData);
        Swal.fire({
          icon: 'success',
          title: 'Criada!',
          text: 'Empresa criada com sucesso',
          showConfirmButton: false,
          timer: 2000
        });
      } else if (modalMode === 'edit' && selectedCompany) {
        await apiService.updateCompany(selectedCompany.id, formData);
        Swal.fire({
          icon: 'success',
          title: 'Atualizada!',
          text: 'Empresa atualizada com sucesso',
          showConfirmButton: false,
          timer: 2000
        });
      }
      await fetchCompanies();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error submitting company:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao salvar empresa';
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: errorMessage,
        confirmButtonColor: '#00d4aa'
      });
      throw error;
    }
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <AdminLayout>
      <Container>
      <Header>
        <Title>Gestão de Empresas</Title>
        <HeaderActions>
          <Button $variant="outline" onClick={handleExport}>
            <Download size={16} />
            Exportar
          </Button>
          <Button $variant="primary" onClick={handleNewCompany}>
            <Plus size={16} />
            Nova Empresa
          </Button>
        </HeaderActions>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total de Empresas</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.active}</StatValue>
          <StatLabel>Empresas Ativas</StatLabel>
        </StatCard>
      </StatsGrid>

      <SearchAndFilters>
        <SearchBox>
          <SearchIcon size={20} />
          <SearchInput
            placeholder="Buscar por empresa, proprietário ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>

        <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Todos os Status</option>
          <option value="active">Ativo</option>
          <option value="pending">Pendente</option>
          <option value="suspended">Suspenso</option>
          <option value="inactive">Inativo</option>
        </FilterSelect>

        <FilterSelect value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
          <option value="all">Todos os Planos</option>
          <option value="Básico">Básico</option>
          <option value="Profissional">Profissional</option>
          <option value="Empresarial">Empresarial</option>
        </FilterSelect>
      </SearchAndFilters>

      <TableContainer>
        <TableHeader>
          <TableTitle>Empresas ({filteredCompanies.length})</TableTitle>
        </TableHeader>

        <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th>Empresa</Th>
              <Th>Plano</Th>
              <Th>Status</Th>
              <Th>Lojas</Th>
              <Th>Funcionários</Th>
              <Th>Clientes</Th>
              <Th>Data de Criação</Th>
              <Th>Ações</Th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.map((company) => (
              <tr key={company.id}>
                <Td>
                  <CompanyInfo>
                    <CompanyAvatar $hasLogo={!!company.logo_url}>
                      {company.logo_url ? (
                        <CompanyLogo src={company.logo_url} alt={company.name} />
                      ) : (
                        company.name.charAt(0)
                      )}
                    </CompanyAvatar>
                    <CompanyDetails>
                      <CompanyName>{company.name}</CompanyName>
                      <CompanyOwner>{company.owner_name}</CompanyOwner>
                    </CompanyDetails>
                  </CompanyInfo>
                </Td>
                <Td>{company.plan?.name || 'Sem plano'}</Td>
                <Td>{getStatusBadge(company.status)}</Td>
                <Td>{company.stores_count ?? 0}</Td>
                <Td>{company.employees_count ?? 0}</Td>
                <Td>{(company.customers_count ?? 0).toLocaleString()}</Td>
                <Td>{new Date(company.created_at).toLocaleDateString('pt-BR')}</Td>
                <Td>
                  <ViewButton onClick={() => handleViewDetails(company)}>
                    <Eye size={14} />
                    Ver
                  </ViewButton>
                  <ActionMenu>
                    <ActionButton onClick={(e) => handleActionClick(company.id, e)}>
                      <MoreHorizontal size={16} />
                    </ActionButton>
                    <DropdownMenu $show={activeDropdown === company.id}>
                      <DropdownItem onClick={() => handleViewDetails(company)}>
                        <Eye size={14} />
                        Ver Detalhes
                      </DropdownItem>
                      <DropdownItem onClick={() => handleEditCompany(company)}>
                        <Edit size={14} />
                        Editar
                      </DropdownItem>
                      <DropdownItem onClick={() => handleSuspendCompany(company)}>
                        {company.status === 'suspended' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {company.status === 'suspended' ? 'Reativar' : 'Suspender'}
                      </DropdownItem>
                      <DropdownItem onClick={() => handleDeleteCompany(company)} style={{ color: '#dc3545' }}>
                        <Trash2 size={14} />
                        Excluir
                      </DropdownItem>
                    </DropdownMenu>
                  </ActionMenu>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        </TableWrapper>

        {filteredCompanies.length === 0 && !isLoading && (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
            <Building2 size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <div>Nenhuma empresa encontrada</div>
          </div>
        )}

        {isLoading && (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
            <RefreshCw size={32} style={{ marginBottom: '1rem', opacity: 0.5, animation: 'spin 1s linear infinite' }} />
            <div>Carregando empresas...</div>
          </div>
        )}
      </TableContainer>
      </Container>

      <CompanyModal
        show={showModal}
        mode={modalMode}
        company={selectedCompany}
        plans={plans}
        onClose={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
      />
    </AdminLayout>
  );
};
